import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil, finalize } from 'rxjs';
import { WidgetDirective } from 'src/app/components/project-pages/dashboard/widgets/widget.directive';
import { ALL_ROLES, GalleryComponent, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { WidgetTitleComponent } from 'src/app/components/project-pages/dashboard/widget-title/widget-title.component';
import { WidgetLoadingComponent } from 'src/app/components/project-pages/dashboard/widget-loading/widget-loading.component';

@Component({
  selector: 'app-image',
  imports: [
    WidgetLoadingComponent,
    WidgetTitleComponent,
    CommonModule
  ],
  standalone: true,
  templateUrl: './image.component.html',
  styleUrl: './image.component.scss'
})

export class ImageComponent extends WidgetDirective {
  // Services spécifiques à ImageComponent
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly dialog: MatDialog = inject(MatDialog);

  // Variables spécifiques à ImageComponent
  containerStyle: string = "";
  blockStyle: string = "width:100%;display:flex;flex-direction:column;";
  titleStyle: string = "font-size:16px;margin:0px 20px 20px;";
  imageStyle: string = "";
  titlePosition: string = "top";
  dataSub: any;
  ready = false;
  image: any;
  blockValue: string = "";
  refreshInterval: NodeJS.Timeout | null = null;
  previousInterval: number | null = null;
  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      crane: { ai: ALL_ROLES, manual: ALL_ROLES },
      worker: { ai: ALL_ROLES, manual: ALL_ROLES },
      action: { ai: ALL_ROLES, manual: ALL_ROLES },
      zone: { ai: ALL_ROLES, manual: ALL_ROLES },
      risk: { ai: ['dev', 'admin'], manual: ['dev', 'admin'] }
    }
  };
  edited: boolean = false;
  private localData: { image: any; blockValue: string } | null = null;

  /////////////////////////////////////////////
  // Angular lifecycle hooks parent override //
  /////////////////////////////////////////////

  override ngOnInit(): void {
    this.resetStyles();
    super.ngOnInit();
  }

  override ngOnChanges(): void {
    if (this.widget && !this.resize) {
      if (this.localData) {
        this.widgetData = this.localData;
      }

      super.ngOnChanges();

      if (this.localData) {
        this.widgetData = this.localData;
      }
    }
  }

  override updateData(): void {
    this.localData = null;
    this.init();
  }

  init() {
    if (!this.widget || !this.widget.visible || this.resize) return;

    this.widgetDesign = this.widget.design;
    this.widgetData = this.widget.data;
    this.widgetQuery = this.widget.query;

    if (this.type !== this.widget.type) {
      this.resetStyles();
    }

    this.type = this.widget.type;
    this.initOptions();

    if (this.localData?.image) {
      this.image = this.localData.image;
      this.blockValue = this.localData.blockValue;
      // With a picture: rendered on its load.
      this.widgetRendered = !this.image?.url;
    } else {
      this.updateImage();
    }
  }

  resetStyles() {
    this.blockStyle = "height:100%;width:100%;display:flex;flex-direction:column;justify-content:center;";
    this.titleStyle = "font-size:16px;margin:0px 20px 20px;";
    this.containerStyle = "";
    this.imageStyle = "width:100%;height:auto;";
  }

  initOptions() {
    try {
      this.resetStyles();
      let options = this.widgetDesign;

      if (!options) return

      this.containerStyle = `background-color: var(--background-color);`
      if (options.title) {
        this.titlePosition = options.title.position || 'top';
        this.showTitle = options.title.display == undefined ? true : options.title.display;
        this.titleStyle = `font-size:${options.title.fontSize || '16'}px; margin:10px 20px 10px; text-align:${options.title.align || 'center'};`;
        this.blockStyle += `height:100%;color: var(--text-color); text-align:${options.title.align || 'center'};`;

        this.imageStyle = options.imageFormat.displayMode;
      }
    } catch (e: any) {
      console.error("Error initializing widget options:", e);
    }
  }

  async updateImage() {
    if (!this.widget?.query) return;

    const query = {
      filters: this.widget.query.filters,
      limit: 1
    };

    // The spinner until the first photo only: the automatic refreshes keep the current one shown.
    const first = !this.image;
    if (first) this.startLoading('image');

    this.attachmentService.getAttachments(query).pipe(takeUntil(this.destroy$), finalize(() => this.stopLoading('image'))).subscribe({
      next: (res) => {
        if (res) {
          this.image = res[0];
          this.blockValue = this.calculateBlockValue();
          this.widgetRendered = !this.image?.url;

          this.localData = {
            image: this.image,
            blockValue: this.blockValue
          };
        }
      },
      error: (err) => {
        console.error(err);
      }
    })


    this.blockValue = this.calculateBlockValue();
    this.autoRefreshImage();
  }

  autoRefreshImage() {
    const { use, rateRefresh, value = 1, unitValue } = this.widget.design.autoRefresh || {};
    const interval = use === "Standart" ? (rateRefresh === "minute" ? 60 : 3600) : (unitValue === "minute" ? value * 60 : unitValue === "hour" ? value * 3600 : value);

    if (this.edited) {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval);
        this.refreshInterval = null;
      }
    }

    if (this.edited || this.previousInterval == interval) {
      return;
    }

    this.previousInterval = interval;

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }

    this.refreshInterval = setInterval(() => {
      this.updateImage();
    }, interval * 1000);
  }

  onImgClick() {
    const getImageInfo = () => {
      return {
        index: 0,
        id: this.image.id,
        name: this.image.name,
        tracker: this.image.tracker,
        url: this.image.url,
        boxes: this.image.predictions,
        startDate: this.image.createdDate,
        disableArrow: true,
        processed: this.image.processed,
        tagDisplayConfig: this.tagDisplayConfig,
        getImageInfo
      }

    }

    this.dialog.open(GalleryComponent, {
      width: "auto",
      height: "auto",
      data: getImageInfo()
    });
  }

  private calculateBlockValue(): string {
    if (this.widgetData && this.widgetData[0]) {
      return this.widgetData[0].value;
    }

    let placeholder = "0";

    if (this.widgetDesign.format && this.widgetDesign.format.placeholder) {
      placeholder = this.widgetDesign.format.placeholder;
    }

    return placeholder;
  }
}